<template>
  <Doughnut ref="chartRef" :data="chartData" :options="mergedOptions" :plugins="[zentrumPlugin]" />
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import type { PropType } from 'vue'
import { Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import type { Chart, ChartData, ChartOptions, Plugin } from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

const props = defineProps({
  chartData: { type: Object as PropType<ChartData<'doughnut'>>, required: true },
  options:   { type: Object as PropType<ChartOptions<'doughnut'>>, default: () => ({}) },
  // Text im Loch des Rings. Ein Kreisdiagramm hat kein Links und kein Rechts —
  // die Mitte ist die Stelle, an der eine Gesamtsumme hier hingehoert.
  centerText: { type: String, default: '' },
})

/*
 * Der Text wird auf die Leinwand gezeichnet, hat also keine CSS-Farbe. Statt
 * eine feste zu waehlen — die in einem der beiden Themen falsch waere — liest
 * das Plugin die vererbte `color` der Leinwand; die setzt das Stylesheet unten
 * aus der Themenfarbe.
 */
const zentrumPlugin: Plugin<'doughnut'> = {
  id: 'zentrumText',
  afterDraw(chart: Chart<'doughnut'>) {
    const text = props.centerText
    if (!text) return

    const { ctx, chartArea } = chart
    if (!chartArea) return

    const x = (chartArea.left + chartArea.right) / 2
    const y = (chartArea.top + chartArea.bottom) / 2

    ctx.save()
    ctx.fillStyle = getComputedStyle(chart.canvas).color || '#000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = '700 15px ' + (getComputedStyle(chart.canvas).fontFamily || 'sans-serif')
    ctx.fillText(text, x, y)
    ctx.restore()
  },
}

// Ohne ein Loch im Ring gibt es keine Mitte, in der etwas stehen koennte.
const mergedOptions = computed<ChartOptions<'doughnut'>>(() => ({
  cutout: '58%',
  ...props.options,
}))

const chartRef = ref<InstanceType<typeof Doughnut> | null>(null)
defineExpose({ get chart() { return chartRef.value?.chart } })
</script>

<style scoped>
/* Quelle der Schriftfarbe fuer das Plugin oben. */
:deep(canvas) {
  color: rgb(var(--v-theme-on-surface));
}
</style>
